import React, { useEffect, useState } from 'react';
import {
  Stack, TextField, IconButton, Typography,
  Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import { Add, Close } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../../../../app/hooks';
import { useUpsertRuleMutation, useDeleteRuleMutation, contractApi } from '../../../../../services/ContractService';
import { RuleCommands } from './RuleCommands';

interface CodeEntry {
  code: string;
  units: number;
}

interface CodingComboCaseRateProps {
  ruleId: string;
  ruleString?: string;
  ruleDescription?: string;
}

/**
 * Parses a stored rule string, handling both legacy (string[]) and current
 * ({ code, units }[]) formats.
 */
function parseRuleString(raw: string): { codes: CodeEntry[]; rate: number } {
  try {
    const r = JSON.parse(raw);
    const rate: number = r.rate ?? 0;

    if (!Array.isArray(r.codes)) return { codes: [], rate };

    const codes: CodeEntry[] = r.codes.map((item: string | { code: string; units?: number }) => {
      if (typeof item === 'string') {
        return { code: item, units: 1 };
      }
      return { code: item.code, units: item.units ?? 1 };
    });

    // Aggregate duplicates (legacy format could have repeated strings)
    const map = new Map<string, number>();
    for (const entry of codes) {
      map.set(entry.code, (map.get(entry.code) ?? 0) + entry.units);
    }
    return { codes: Array.from(map, ([code, units]) => ({ code, units })), rate };
  } catch {
    return { codes: [], rate: 0 };
  }
}

const CodingComboCaseRate: React.FC<CodingComboCaseRateProps> = ({ ruleId, ruleString = '', ruleDescription = '' }) => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const dispatch = useAppDispatch();
  const [rule, setRule] = useState<{ codes: CodeEntry[]; rate: number }>({ codes: [], rate: 0 });
  const [codeInput, setCodeInput] = useState('');
  const [unitsInput, setUnitsInput] = useState(1);
  const [upsertRule] = useUpsertRuleMutation();
  const [deleteRule] = useDeleteRuleMutation();

  const save = async () => {
    if (!contractId) return;

    try {
      await upsertRule({
        contractId: contractId,
        ruleId,
        ruleType: 'CodingComboCaseRate',
        priority: 0,
        ruleString: JSON.stringify(rule),
        description: ruleDescription ?? ''
      }).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to save rule:', error);
    }
  };

  const ruleDelete = async () => {
    try {
      await deleteRule({ ruleId }).unwrap();
      dispatch(contractApi.util.invalidateTags(['RuleList']));
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  useEffect(() => {
    if (ruleString !== '') {
      setRule(parseRuleString(ruleString));
    }
  }, [ruleString, ruleId]);

  const addCode = () => {
    const trimmed = codeInput.trim();
    if (!trimmed) return;

    const units = unitsInput >= 1 ? unitsInput : 1;
    const existing = rule.codes.find(c => c.code === trimmed);

    if (existing) {
      setRule({
        ...rule,
        codes: rule.codes.map(c =>
          c.code === trimmed ? { ...c, units: c.units + units } : c
        )
      });
    } else {
      setRule({ ...rule, codes: [...rule.codes, { code: trimmed, units }] });
    }

    setCodeInput('');
    setUnitsInput(1);
  };

  const updateUnits = (index: number, units: number) => {
    const clamped = Math.max(1, units);
    setRule({
      ...rule,
      codes: rule.codes.map((c, i) => i === index ? { ...c, units: clamped } : c)
    });
  };

  const removeCode = (index: number) => {
    setRule({ ...rule, codes: rule.codes.filter((_, i) => i !== index) });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addCode();
    }
  };

  return (
    <React.Fragment>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Enter CPT codes and required units that must be present on the encounter for this case rate to apply.
      </Typography>

      <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Case Rate:</Typography>
        <TextField
          type="number"
          label="Rate"
          value={rule.rate}
          sx={{ width: '150px' }}
          inputProps={{ step: 0.01, min: 0 }}
          onChange={(e) => setRule({ ...rule, rate: Number(e.target.value) })}
          size="small"
        />
      </Stack>

      {rule.codes.length > 0 && (
        <Table size="small" sx={{ maxWidth: 400, mb: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, py: 0.5 }}>CPT Code</TableCell>
              <TableCell sx={{ fontWeight: 600, py: 0.5 }} align="center">Units</TableCell>
              <TableCell sx={{ py: 0.5 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rule.codes.map((entry, i) => (
              <TableRow key={entry.code}>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="body2">{entry.code}</Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }} align="center">
                  <TextField
                    type="number"
                    size="small"
                    variant="standard"
                    value={entry.units}
                    inputProps={{ min: 1, step: 1, style: { textAlign: 'center', width: 40 } }}
                    onChange={(e) => updateUnits(i, Number(e.target.value))}
                  />
                </TableCell>
                <TableCell sx={{ py: 0.5 }} align="right">
                  <IconButton size="small" onClick={() => removeCode(i)}>
                    <Close fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {rule.codes.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          No CPT codes added yet. Use the fields below to add codes.
        </Typography>
      )}

      <Stack direction="row" gap={1} alignItems="flex-start">
        <TextField
          label="CPT Code"
          value={codeInput}
          sx={{ width: '150px' }}
          onChange={(e) => setCodeInput(e.target.value)}
          onKeyDown={handleKeyDown}
          size="small"
        />
        <TextField
          type="number"
          label="Units"
          value={unitsInput}
          sx={{ width: '80px' }}
          inputProps={{ step: 1, min: 1 }}
          onChange={(e) => setUnitsInput(Math.max(1, Number(e.target.value)))}
          onKeyDown={handleKeyDown}
          size="small"
        />
        <IconButton onClick={addCode} color="primary" size="small" sx={{ mt: 0.5 }}>
          <Add />
        </IconButton>
      </Stack>

      <RuleCommands
        saveCommand={save}
        deleteCommand={ruleDelete}
        isNewRule={ruleId === '0'}
      />
    </React.Fragment>
  );
};

export { CodingComboCaseRate };
